import { useCallback } from 'react';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import type { BaseEditor } from 'slate';
import type { EditableProps } from '../core';

type ScrollSelectionIntoView = NonNullable<EditableProps['scrollSelectionIntoView']>;
type PatchableBoundingClientRectTarget = Element & {
  getBoundingClientRect?: Element['getBoundingClientRect'];
};

export const useScrollSelectionIntoView = (
  _editor: BaseEditor
): { scrollSelectionIntoView: ScrollSelectionIntoView } => {
  const scrollSelectionIntoView = useCallback<ScrollSelectionIntoView>((_editor, domRange) => {
    try {
      const target =
        domRange.startContainer instanceof Element
          ? domRange.startContainer
          : domRange.startContainer.parentElement;

      if (target == null || domRange.getBoundingClientRect == null) {
        return;
      }

      const patchTarget = target as PatchableBoundingClientRectTarget;
      const hadOwnGetBoundingClientRect = Object.prototype.hasOwnProperty.call(
        target,
        'getBoundingClientRect'
      );
      const originalGetBoundingClientRect = target.getBoundingClientRect;

      try {
        patchTarget.getBoundingClientRect = domRange.getBoundingClientRect.bind(domRange);

        scrollIntoViewIfNeeded(target, {
          behavior: 'auto',
          scrollMode: 'if-needed',
          block: 'nearest',
          inline: 'nearest',
        });
      } finally {
        if (hadOwnGetBoundingClientRect) {
          patchTarget.getBoundingClientRect = originalGetBoundingClientRect;
        } else {
          // Remove the temporary own property so lookup falls back to Element.prototype.
          // Using Reflect avoids TypeScript's delete-operand restriction here.
          Reflect.deleteProperty(patchTarget, 'getBoundingClientRect');
        }
      }
    } catch (error) {
      // no-op, this can happen if Slate runs headlessly without an associated DOM
    }
  }, []);

  return { scrollSelectionIntoView };
};
